import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Breadcrumb, Card, Button, Tag, Typography, Row, Col, Space, Descriptions } from 'antd';
import * as Icon from 'react-feather';
import {
  curatedContentActionStarts,
  curatedContentActionErrors,
  getCuratedContentByIdAction,
} from '../../core/data/redux/modules/curatedContent/actions';
import { getCuratedContentById } from '../../services/curatedContentServices';

const { Title, Text, Paragraph } = Typography;

const CuratedContentDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { currentCuratedContent, loading, error } = useSelector((state: any) => state.curatedContent);

  useEffect(() => {
    if (id) {
      fetchCuratedContent();
    }
  }, [id]);

  const fetchCuratedContent = async () => {
    dispatch(curatedContentActionStarts());
    try {
      const res = await getCuratedContentById(id!);
      if (res.error === true) throw new Error(res.message || 'Error fetching curated content');
      dispatch(getCuratedContentByIdAction(res.data));
    } catch (err: any) {
      dispatch(curatedContentActionErrors(err.message));
    }
  };

  if (loading) return <Card loading style={{ margin: 24 }} />;
  if (error) return <Card style={{ margin: 24 }}><Text type="danger">{error}</Text></Card>;
  if (!currentCuratedContent) return <Card style={{ margin: 24 }}><Text>Curated content not found</Text></Card>;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PUBLISHED': return 'green';
      case 'DRAFT': return 'orange';
      case 'ARCHIVED': return 'red';
      default: return 'default';
    }
  };

  return (
    <div className="page-wrapper page-settings" style={{ paddingTop: 0 }}>
      <div className="content">
        <Breadcrumb style={{ marginBottom: 20 }}>
          <Breadcrumb.Item>
            <Link to="/admin/curated-content">Curated Content</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Curated Content Detail</Breadcrumb.Item>
        </Breadcrumb>
        
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <Title level={4} style={{ margin: 0 }}>Curated Content Details</Title>
            <Space>
              <Link to={`/admin/curated-content/edit-curated-content/${id}`}>
                <Button icon={<Icon.Edit size={16} />}>Edit</Button>
              </Link>
            </Space>
          </div>

          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Card title="Content" size="small">
                <Paragraph>{currentCuratedContent.content}</Paragraph>
              </Card>
            </Col>
            
            <Col span={12}>
              <Card title="Basic Information" size="small">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="ID">{currentCuratedContent.id}</Descriptions.Item>
                  <Descriptions.Item label="Type">
                    <Tag color="blue">{currentCuratedContent.type}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={getStatusColor(currentCuratedContent.publish_status)}>
                      {currentCuratedContent.publish_status}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Impressions">
                    {currentCuratedContent.impression_count || 0}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            <Col span={12}>
              <Card title="Media & Links" size="small">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Image URL">
                    {currentCuratedContent.image_url ? (
                      <a href={currentCuratedContent.image_url} target="_blank" rel="noopener noreferrer">
                        View Image
                      </a>
                    ) : 'None'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Link URL">
                    {currentCuratedContent.link_url ? (
                      <a href={currentCuratedContent.link_url} target="_blank" rel="noopener noreferrer">
                        Open Link
                      </a>
                    ) : 'None'}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            <Col span={24}>
              <Card title="Timestamps" size="small">
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="Created At">
                    {new Date(currentCuratedContent.created_at).toLocaleString()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Updated At">
                    {new Date(currentCuratedContent.updated_at).toLocaleString()}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
          </Row>
        </Card>
      </div>
    </div>
  );
};

export default CuratedContentDetail;
